/**
 * ReceiptView — public receipt page, mounted at /r/:token (no auth).
 *
 * This is what a customer sees when they click the WhatsApp / email link.
 * It displays the receipt in a clean, mobile-friendly layout with a
 * download button for the PDF.
 *
 * Mount in App.tsx OUTSIDE the auth-required routes:
 *
 *   <Route path="/r/:token" element={<ReceiptView />} />
 */
import React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Receipt as ReceiptIcon,
  Download,
  Building2,
  Phone,
  Loader2,
  AlertCircle,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { receiptService } from "@/services/receiptService";
import { formatAmount } from "@/lib/currency";

const ReceiptView: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["public-receipt", token],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return receiptService.getPublicReceipt(token);
    },
    enabled: Boolean(token),
    retry: false,
  });

  // ── Loading ─────────────────────────────────────────────────
  if (isLoading) {
    return (
      <PageShell>
        <div className="text-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
          <p className="text-sm">Loading receipt…</p>
        </div>
      </PageShell>
    );
  }

  // ── Error (404 / 410 / etc.) ────────────────────────────────
  if (isError || !data) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    const isGone = status === 410;
    const msg = isGone
      ? "This receipt link has been revoked or expired."
      : "Receipt not found.";
    return (
      <PageShell>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-10 w-10 text-rose-500 mx-auto mb-3" />
            <p className="text-base font-medium mb-1">{msg}</p>
            <p className="text-xs text-muted-foreground">
              If you think this is a mistake, contact the business that issued
              the receipt.
            </p>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  // ── Render ──────────────────────────────────────────────────
  const r = data;
  const total = formatMoney(r.totalMinor, r.currency);

  return (
    <PageShell>
      <Card>
        <CardContent className="p-0">
          {/* Hero */}
          <div className="px-5 py-6 border-b text-center">
            {r.business?.logoUrl && (
              <img
                src={r.business.logoUrl}
                alt={r.business.name || "Business"}
                className="h-12 mx-auto mb-3 object-contain"
              />
            )}
            <h1 className="text-lg font-semibold text-foreground">
              {r.business?.name || "Receipt"}
            </h1>
            {r.business?.address && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {r.business.address}
              </p>
            )}
            <div className="flex items-center justify-center gap-3 text-[10px] text-muted-foreground mt-1.5 flex-wrap">
              {r.business?.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-2.5 w-2.5" />
                  {r.business.phone}
                </span>
              )}
              {r.business?.tin && <span>TIN: {r.business.tin}</span>}
            </div>
          </div>

          {/* Meta */}
          <div className="px-5 py-4 grid grid-cols-2 gap-y-2 text-sm border-b">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Receipt
              </div>
              <div className="font-semibold tabular-nums">
                {r.receiptNumber}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 justify-end">
                <Calendar className="h-2.5 w-2.5" />
                Issued
              </div>
              <div className="text-xs">{formatDateTime(r.issuedAt)}</div>
            </div>
            {r.customer?.name && (
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Billed to
                </div>
                <div className="text-sm font-medium">{r.customer.name}</div>
              </div>
            )}
          </div>

          {/* Line items */}
          <div className="px-5 py-4 border-b">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
              Items
            </div>
            <div className="space-y-2">
              {r.lineItems.map((li, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {li.name}
                    </div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">
                      {li.quantity} ×{" "}
                      {formatMoney(li.unitPriceMinor, r.currency)}
                    </div>
                  </div>
                  <div className="text-sm font-semibold tabular-nums shrink-0">
                    {formatMoney(li.lineTotalMinor, r.currency)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals + tax */}
          <div className="px-5 py-4 space-y-1 text-sm">
            {r.subtotalMinor !== undefined && (
              <SummaryRow
                label="Subtotal"
                value={formatMoney(r.subtotalMinor, r.currency)}
              />
            )}
            {r.discountMinor !== undefined && r.discountMinor > 0 && (
              <SummaryRow
                label="Discount"
                value={`-${formatMoney(r.discountMinor, r.currency)}`}
              />
            )}
            {(r.taxBreakdown || []).map((t, i) => {
              const label = t.name || t.code || "Tax";
              const rate = t.rate ? ` (${(t.rate * 100).toFixed(1)}%)` : "";
              const amt = t.amountMinor ?? Math.round((t.amount || 0) * 100);
              return (
                <SummaryRow
                  key={i}
                  label={`${label}${rate}`}
                  value={formatMoney(amt, r.currency)}
                  muted
                />
              );
            })}
            <div className="flex items-center justify-between pt-2 border-t mt-2">
              <span className="text-base font-bold">Total</span>
              <span className="text-lg font-bold tabular-nums">{total}</span>
            </div>
          </div>

          {/* Payments */}
          {r.payments.length > 0 && (
            <div className="px-5 py-4 border-t bg-muted/30">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                Payment
              </div>
              <div className="space-y-1.5 text-sm">
                {r.payments.map((p, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span>{p.method}</span>
                    <span className="tabular-nums font-medium">
                      {formatMoney(p.amountMinor, r.currency)}
                    </span>
                  </div>
                ))}
                {r.balanceMinor !== undefined && r.balanceMinor > 0 && (
                  <div className="flex items-center justify-between font-semibold">
                    <span>Balance due</span>
                    <span className="tabular-nums">{formatMoney(r.balanceMinor, r.currency)}</span>
                  </div>
                )}
                {r.changeMinor !== undefined && r.changeMinor > 0 && (
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Change</span>
                    <span className="tabular-nums">
                      {formatMoney(r.changeMinor, r.currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action */}
          <div className="px-5 py-4 border-t flex justify-center">
            <Button
              asChild
              className="bg-[#4CAF50] hover:bg-[#45A049] text-white"
            >
              <a href={receiptService.publicPdfUrl(token!)} download>
                <Download className="h-4 w-4 mr-2" />
                Download PDF receipt
              </a>
            </Button>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 text-center text-[10px] text-muted-foreground border-t bg-card">
            Thank you for your business.
          </div>
        </CardContent>
      </Card>

      <p className="text-center text-[10px] text-muted-foreground mt-4">
        Powered by{" "}
        <Link to="/" className="font-medium text-[#4CAF50] hover:underline">
          Eaze
        </Link>
      </p>
    </PageShell>
  );
};

// ── Layout shell ────────────────────────────────────────────────

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-muted/30 py-6 px-4">
    <div className="max-w-md mx-auto">
      <div className="flex items-center justify-center gap-2 mb-5 text-muted-foreground">
        <ReceiptIcon className="h-4 w-4" />
        <span className="text-xs font-medium uppercase tracking-wider">
          Receipt
        </span>
      </div>
      {children}
    </div>
  </div>
);

const SummaryRow: React.FC<{
  label: string;
  value: string;
  muted?: boolean;
}> = ({ label, value, muted }) => (
  <div
    className={`flex items-center justify-between ${
      muted ? "text-xs text-muted-foreground" : ""
    }`}
  >
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

// ── Helpers ─────────────────────────────────────────────────────

function formatMoney(minor: number, currency: string): string {
  return formatAmount((minor || 0) / 100, currency);
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default ReceiptView;
